import { collection, query, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * Egyedi dokumentum szám generálása
 * @param collectionName - A Firestore kollekció neve
 * @param prefix - A szám prefix-e (pl. "MR", "SL", "AJ")
 * @param year - Év (opcionális, alapértelmezett az aktuális év)
 * @returns Promise<string> - Az egyedi szám
 */
export async function generateUniqueNumber(
  collectionName: string, 
  prefix: string = '', 
  year?: number
): Promise<string> {
  try {
    const currentYear = year || new Date().getFullYear();
    const yearString = currentYear.toString();
    
    // Egyszerűsített query - csak az összes dokumentumot lekérjük és kliens oldalon szűrjük
    // Ez elkerüli a komplex Firestore index szükségességet
    let q;
    try {
      q = query(
        collection(db, collectionName),
        orderBy('number', 'desc'),
        limit(50) // Csak az utolsó 50 dokumentumot vizsgáljuk
      );
    } catch (error) {
      // Ha a collection nem létezik vagy nincs 'number' mező, akkor alapértelmezett számot adunk vissza
      console.warn(`Collection ${collectionName} not found or no 'number' field, starting with 1`);
      const paddedNumber = '1'.padStart(4, '0');
      return `${prefix}${yearString}${paddedNumber}`;
    }

    const querySnapshot = await getDocs(q);
    let nextNumber = 1;

    if (!querySnapshot.empty) {
      // Kliens oldalon szűrjük az aktuális évre
      const currentYearDocs = querySnapshot.docs
        .map(doc => doc.data().number)
        .filter(number => number && number.startsWith(`${prefix}${yearString}`))
        .sort()
        .reverse();

      if (currentYearDocs.length > 0) {
        const lastNumber = currentYearDocs[0];
        
        // Kivonjuk a sorszámot a végéről
        const regex = new RegExp(`^${prefix}${yearString}(\\d+)$`);
        const match = lastNumber.match(regex);
        
        if (match) {
          nextNumber = parseInt(match[1]) + 1;
        }
      }
    }

    // Formázzuk a számot (4 jegyű padding)
    const paddedNumber = nextNumber.toString().padStart(4, '0');
    
    return `${prefix}${yearString}${paddedNumber}`;

  } catch (error) {
    console.error('Error generating unique number:', error);
    // Fallback: timestamp alapú szám
    const timestamp = Date.now().toString().slice(-6);
    return `${prefix}${new Date().getFullYear()}${timestamp}`;
  }
}

/**
 * Árajánlat szám generálása
 */
export async function generateQuotationNumber(): Promise<string> {
  return generateUniqueNumber('quotations', 'AJ');
}

/**
 * Megrendelés szám generálása
 */
export async function generateOrderNumber(): Promise<string> {
  return generateUniqueNumber('orders', 'MR');
}

/**
 * Szállítólevél szám generálása
 */
export async function generateDeliveryNoteNumber(): Promise<string> {
  return generateUniqueNumber('delivery_notes', 'SL');
}

/**
 * Számla szám generálása
 */
export async function generateInvoiceNumber(): Promise<string> {
  return generateUniqueNumber('invoices', 'SZ');
}

/**
 * Általános dokumentum szám generálása típus alapján
 */
export async function generateDocumentNumber(documentType: string): Promise<string> {
  switch (documentType.toLowerCase()) {
    case 'quotation':
    case 'ajanlat':
      return generateQuotationNumber();
    
    case 'order':
    case 'megrendeles':
      return generateOrderNumber();
    
    case 'delivery_note':
    case 'szallitolevel':
      return generateDeliveryNoteNumber();
    
    case 'invoice':
    case 'szamla':
      return generateInvoiceNumber();
    
    default:
      return generateUniqueNumber('documents', 'DOC');
  }
} 